import {
  Archive,
  Braces,
  Calendar,
  CircleCheck,
  Database,
  FilePen,
  FileText,
  LucideIcon,
  Mail,
  MessageSquare,
  RotateCcw,
  SearchCheck,
  Slack,
  TriangleAlert,
  User,
  UserPlus
} from 'lucide-react';
import React from 'react';
import { ReactComponent as TeamsIcon } from '../../../../../icons/microsoft_teams_icon.svg';

type Entry =
  | { kind: 'lucide'; Icon: LucideIcon }
  | { kind: 'svgComponent'; Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }
  | { kind: 'image'; src: string };

/** One entry per `newCopy.ts` category string (see `docs/issues-redesign-plan.md`'s
 * source CSV for the canonical category list). Brand logos render at their own
 * colors; lucide icons inherit `currentColor` (no explicit `color` prop), so they
 * match whatever text color surrounds them at each call site. */
const CATEGORY_ICON: Record<string, Entry> = {
  'Background Check Invitation failed': { kind: 'lucide', Icon: SearchCheck },
  'Calendar invite failed': { kind: 'lucide', Icon: Calendar },
  'Data sync failed': { kind: 'lucide', Icon: Database },
  'Document creation failed': { kind: 'lucide', Icon: FileText },
  'Email bounced': { kind: 'lucide', Icon: Mail },
  'Email undeliverable': { kind: 'lucide', Icon: Mail },
  'Enrollment into another experience failed': { kind: 'lucide', Icon: UserPlus },
  'Google Groups invite failed': { kind: 'image', src: '/images/integrations-logos/google_group.svg' },
  'HTTP Post request failed': { kind: 'lucide', Icon: Braces },
  'Jira ticket creation failed': { kind: 'image', src: '/images/integrations-logos/jira-icon.svg' },
  'Lessonly assignment failed': { kind: 'image', src: '/images/integrations-logos/lessonly-icon.webp' },
  'Missing supporting actor': { kind: 'lucide', Icon: User },
  'Okta Groups invite failed': { kind: 'image', src: '/images/integrations-logos/okta_aura.png' },
  'Outlook Groups invite failed': { kind: 'image', src: '/images/integrations-logos/outlook-logo.svg' },
  'Re-enrollment failed': { kind: 'lucide', Icon: RotateCcw },
  'SMS undeliverable': { kind: 'lucide', Icon: MessageSquare },
  'Signature request failed': { kind: 'lucide', Icon: FilePen },
  'Slack channel invite failed': { kind: 'lucide', Icon: Slack },
  'Slack message undeliverable': { kind: 'lucide', Icon: Slack },
  'Supporting Actor assignment failed': { kind: 'lucide', Icon: User },
  'Task issue': { kind: 'lucide', Icon: CircleCheck },
  'Teams invite failed': { kind: 'svgComponent', Icon: TeamsIcon },
  'Teams message undeliverable': { kind: 'svgComponent', Icon: TeamsIcon },
  'Unenrollment from this experience failed': { kind: 'lucide', Icon: Archive },
  'Unknown error': { kind: 'lucide', Icon: TriangleAlert }
};

export const CategoryIcon: React.FC<{ category: string; size?: number }> = ({ category, size = 16 }) => {
  const entry = CATEGORY_ICON[category];
  if (!entry) return null;
  if (entry.kind === 'lucide') {
    const { Icon } = entry;
    return <Icon size={size} strokeWidth={1.5} />;
  }
  if (entry.kind === 'svgComponent') {
    const { Icon } = entry;
    return <Icon width={size} height={size} />;
  }
  return <img src={entry.src} width={size} height={size} alt="" />;
};
